import { FC, useState } from 'react';
import { StepViewModel, IntegrationType, MultipleStepExecutionMode } from '../../models/waterfall';
import { StepDetailsDialog } from './StepDetailsDialog';
import { StepNode } from './StepNode';
import { StepNodeActionButton } from './StepNodeActionButton';
import { areAllStepsValid, createEmptyStep } from './stepUtils';
import { useIntegrationSettingsByType } from '../../hooks/useIntegrationSettingsByType';
import { noop } from '../../utils/misc-utils';
import { Dropdown } from 'primereact/dropdown';
import styles from './StepEditor.module.css';

interface StepEditorProps {
  initialSteps?: StepViewModel[] | null;
  onUpdateStep?: (step: StepViewModel) => Promise<void> | void;
  onAddStep?: (parentStep: StepViewModel) => void;
  onDeleteStep?: (step: StepViewModel) => void;
  onAddInitialStep?: () => void;
  onReorderStep?: (step: StepViewModel, direction: 'up' | 'down') => void;
  onDeleteInitialStep?: (stepGuid: string) => void;
  initialStepsExecutionMode?: MultipleStepExecutionMode;
  onInitialStepsExecutionModeChange?: (mode: MultipleStepExecutionMode) => void;
  integrationType: IntegrationType;
}

const executionModeOptions = [
  { label: 'All Matching', value: MultipleStepExecutionMode.All },
  { label: 'First Matching', value: MultipleStepExecutionMode.First },
];

export const StepEditor: FC<StepEditorProps> = ({ 
  initialSteps, 
  onUpdateStep, 
  onAddStep, 
  onDeleteStep, 
  onReorderStep,
  onAddInitialStep,
  onDeleteInitialStep,
  initialStepsExecutionMode,
  onInitialStepsExecutionModeChange,
  integrationType,
}) => {
  const [selectedStep, setSelectedStep] = useState<StepViewModel | null>(null);
  const [isPanelVisible, setIsPanelVisible] = useState(false);

  // Integration settings supply vendor names for the steps.
  const { data: integrationSettings = [] } = useIntegrationSettingsByType(integrationType);

  const handleStepClick = (step: StepViewModel, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedStep(step);
    setIsPanelVisible(true);
  };

  const handleClosePanel = () => {
    setIsPanelVisible(false);
    setSelectedStep(null);
  };

  const handleSaveStep = async (updatedStep: StepViewModel) => {
    try {
      await onUpdateStep?.(updatedStep);
      handleClosePanel();
    } catch (error) {
      noop(error);
    }
  };

  const handleAddStepClick = (parentStep: StepViewModel, e: React.MouseEvent) => {
    e?.stopPropagation();
    onAddStep?.(parentStep);
  };

  const handleDeleteStepClick = (step: StepViewModel, e: React.MouseEvent) => {
    e?.stopPropagation();
    onDeleteStep?.(step);
  };

  const handleReorderStepClick = (step: StepViewModel, direction: 'up' | 'down', e: React.MouseEvent) => {
    e?.stopPropagation();
    onReorderStep?.(step, direction);
  };

  // Create a delete handler for each initial step tree
  // - For the root initial step: delete from the initialSteps array
  // - For child steps: use the regular onDeleteStep handler
  const createDeleteHandler = (initialStepGuid: string, canDeleteInitialStep: boolean) => {
    return (step: StepViewModel, e: React.MouseEvent) => {
      e?.stopPropagation();
      // If this is the root initial step being deleted
      if (step.stepGuid === initialStepGuid && canDeleteInitialStep) {
        onDeleteInitialStep?.(step.stepGuid);
      } else {
        // Otherwise it's a child step, use the regular delete handler
        onDeleteStep?.(step);
      }
    };
  };

  const hasSteps = initialSteps && initialSteps.length > 0;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-xl font-semibold m-0">
            Initial Steps <span className="font-normal text-gray-400">({initialSteps?.length || 0})</span>
          </h3>
          <StepNodeActionButton
            onClick={(e) => {
              e.stopPropagation();
              onAddInitialStep?.();
            }}
            title="Add Initial Step"
            variant="add"
          />
        </div>
        {initialSteps && initialSteps.length > 1 && (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-color-secondary">Execution Mode:</label>
            <Dropdown
              value={initialStepsExecutionMode}
              options={executionModeOptions}
              onChange={(e) => onInitialStepsExecutionModeChange?.(e.value)}
              className="w-auto"
              style={{ minWidth: '150px' }}
              appendTo={document.body}
            />
          </div>
        )}
      </div>

      <div className={`border border-surface rounded-lg bg-slate-50 p-6 ${styles.mainContainer}`}>
        {hasSteps ? (
          <div className={styles.stepsContainer}>
            {initialSteps.map((step, index) => {
              const canDeleteInitialStep = initialSteps.length > 1;
              // Calculate validity for this specific initial step tree only
              const isThisStepTreeValid = areAllStepsValid(step);
              return (
                <div key={step.stepGuid} className={styles.stepColumn}>
                  {index > 0 && (
                    <div className={styles.separator} />
                  )}
                  <div className="flex-1">
                    <StepNode
                      step={step}
                      level={0}
                      onStepClick={handleStepClick}
                      onAddStepClick={handleAddStepClick}
                      onDeleteStepClick={createDeleteHandler(step.stepGuid, canDeleteInitialStep)}
                      onReorderStepClick={handleReorderStepClick}
                      isRootDeletable={canDeleteInitialStep}
                      allStepsValid={isThisStepTreeValid}
                      integrationSettings={integrationSettings}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-color-secondary py-8">
            <i className="pi pi-inbox text-4xl mb-3"></i>
            <p className="text-lg">No initial steps configured yet</p>
            <p className="text-sm">Click "Add Initial Step" to get started</p>
          </div>
        )}
      </div>

      <StepDetailsDialog step={selectedStep} visible={isPanelVisible} onClose={handleClosePanel} onSave={handleSaveStep} integrationType={integrationType} />
    </div>
  );
};
